<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { addonInputTypes } from 'src/helpers/addons/addon_helpers';
	import { verifySettingUnsupportedFWVersion } from 'src/helpers/firmware_helpers';
	import { isPlusUni } from 'src/helpers/interface_helpers';
	import { devices_config } from '@devices/index';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Design system components
	import { Input, Text } from '@Shelly/shelly-design-system';
	//Types
	import { valueOutOfDecimalRange, valueOutOfRange } from 'src/helpers/utilities';
	import type { PeripheralThresholdSettings, PeripheralTypes } from 'src/types/Peripherals';
	import type { Device } from 'src/types/Device';

	let className = '';
	export { className as class };

	export let settings: PeripheralThresholdSettings,
		type: PeripheralTypes,
		icon: string = '',
		device: Device,
		error = '';

	const deviceDefaults = devices_config?.[device?.type]?.addons_options?.[type];

	let reportThrMinDecimalDigits =
		type == 'voltmeter' &&
		isPlusUni(device?.type) &&
		!verifySettingUnsupportedFWVersion(device, devices_settings.getByRealId(device.id), '1.3.0')
			? 3
			: 2;

	if (deviceDefaults?.reportThrMinDecimalDigits) {
		reportThrMinDecimalDigits = deviceDefaults.reportThrMinDecimalDigits;
	}

	const inputType = type == 'input' ? addonInputTypes[type].analog : addonInputTypes[type];
	const min = deviceDefaults?.['thrMin'] || (reportThrMinDecimalDigits == 3 ? 0.01 : inputType.thrMin);
	const max = deviceDefaults?.['thrMax'] || inputType.thrMax;
	const unit = (type == 'temperature' ? '°' : '') + inputType.unit;
	const infoLabels = {
		temperature: {
			i18n: 'peripherals_temperature_change',
			default:
				'Temperature change with more than the specified value in Celsius triggers sensor data report.',
		},
		humidity: {
			i18n: 'peripherals_humidity_change',
			default:
				'Relative Humidity change with more than the specified value triggers the device to report sensor data.',
		},
	};

	$: error = valueOutOfRange(
		Number(type == 'temperature' ? settings.report_thr_C : settings.report_thr),
		min,
		max,
	)
		? $format('min_max_error_description', {
				values: { min: min + unit, max: max + unit },
				default: `Value must be between ${min} and ${max}.`,
			})
		: valueOutOfDecimalRange(
				type == 'temperature' ? settings.report_thr_C : settings.report_thr,
				reportThrMinDecimalDigits,
			);
</script>

<div class={className}>
	{#if infoLabels[type]}
		<Text styleType="body-small" class="mb-s">
			{$format(infoLabels[type].i18n, {
				values: { min, max },
				default: infoLabels[type].default,
			})}
		</Text>
	{/if}
	{#if type == 'temperature'}
		<Input
			aria-label={$format('input_temperature_threshold', {
				default: 'Input temperature threshold',
			})}
			size="m"
			class="mb-s"
			{icon}
			bind:value={settings.report_thr_C}
			{error}
			id="input-whta745"
		/>
	{:else}
		<Input
			aria-label={$format('input_temperature_threshold', {
				default: 'Input temperature threshold',
			})}
			size="m"
			class="mb-s"
			{icon}
			bind:value={settings.report_thr}
			{error}
			id="input-jclcadm"
		/>
	{/if}
	<Text styleType="body-small">
		{$format('min_max_unit_range', {
			default: `(Minimum: ${min + unit}, Maximum: ${max + unit})`,
			values: { min: min + unit, max: max + unit },
		})}
	</Text>
</div>
